/**
 * Analytics Hooks
 * 
 * 관심사별로 분리된 추적 훅들을 제공합니다.
 * 각 파일에서 개별적으로 import하거나, 이 index에서 한번에 import할 수 있습니다.
 * 
 * @example
 * // 전체 import (기존 방식, 하위 호환)
 * import { useTabTracking, useReportLike } from 'lib/analytics/react/hooks';
 * 
 * // 개별 import (권장)
 * import { useTabTracking } from 'lib/analytics/react/hooks/tabs';
 * import { useReportLike } from 'lib/analytics/react/hooks/report';
 */

// 🔍 검색 관련
export {
    useSearchTracking,
    useTrackedSearchResults,
} from "./search";

// 📑 탭 관련
export {
    useTabTracking,
    useMarketTabTracking,
} from "./tabs";

// 📄 리포트 관련
export {
    useReportLike,
    useReportShare,
} from "./report";



// 📊 전략 관련
export {
    useStrategyHistoryNav,
    useStrategyChartClick,
    useStrategyTracking, // deprecated
} from "./strategy";

// 💼 포트폴리오 관련
export {
    usePortfolioCreate,
    usePortfolioEdit,
    usePortfolioTracking, // deprecated
} from "./portfolio";

// 하위 호환성을 위한 import
import { useReportLike } from "./report";
import { useReportShare } from "./report";

/**
 * @deprecated
 * useEngagementTracking은 개별 훅으로 분리되었습니다.
 * 대신 useReportLike, useReportShare, useWatchlist('add'), useWatchlist('remove')를 사용하세요.
 */
export function useEngagementTracking() {
    const { trackLike } = useReportLike();
    const { trackShare } = useReportShare();

    return {
        trackLike,
        trackShare,
    };
}

// Intent 관련 훅 export
export {
    useIntent,
    type IntentOptions,
    type IntentActions,
    type IntentStartParams,
    type IntentSuccessParams,
    type IntentFailParams,
} from './intent/useIntent';

// Report Intent 훅
export { useReportRequest, type ReportRequestContext, type ReportRequestActions } from './intent/report/useReportRequest';
export { useVote, type VoteContext, type VoteActions } from './intent/report/useVote';
export { useReportCommentLike, type ReportCommentLikeContext, type ReportCommentLikeActions } from './intent/report/useReportCommentLike';
export { useReportComment, type ReportCommentContext, type ReportCommentActions } from './intent/report/useReportComment';

// Watchlist Intent 훅
export {
    useWatchlist,
    useWatchlistAdd,
    useWatchlistRemove,
    type WatchlistAction,
    type WatchlistContext,
    type WatchlistActions,
    type WatchlistBothActions
} from './intent/useWatchlist';

// Alarm Intent 훅
export {
    useStockAlarm,
    useStrategyAlarm,
    useReportAlarm,
    type StockAlarmContext,
    type StockAlarmActions,
    type StrategyAlarmContext,
    type StrategyAlarmActions,
    type ReportAlarmContext,
    type ReportAlarmActions,
} from './intent/useAlarm';

// Auth Intent 훅
export {
    useAuth,
    type AuthStartContext,
    type AuthOutcomeContext,
    type AuthActions,
} from './intent/useAuth';

// Signup Step 훅
export {
    useSignupStep,
    type SignupStepKey,
    type SignupStepViewParams,
    type SignupStepSubmitParams,
    type SignupStepActions,
} from './intent/useSignupStep';

// Portfolio Intent 훅
export {
    usePortfolioFlow,
    type PortfolioFlowContext,
    type PortfolioFlowActions,
} from './intent/portfolio/usePortfolioFlow';

export {
    usePortfolioStep,
    type PortfolioStepKey,
    type PortfolioStepViewParams,
    type PortfolioStepSubmitParams,
    type PortfolioStepActions,
} from './intent/portfolio/usePortfolioStep';

export {
    usePortfolioCompute,
    type PortfolioComputeContext,
    type PortfolioComputeActions,
} from './intent/portfolio/usePortfolioCompute';

export {
    usePortEdit,
    type PortEditStartContext,
    type PortEditSuccessContext,
    type PortEditActions,
    type PortfolioItem,
} from './intent/portfolio/usePortEdit';

export {
    usePortfolioDelete,
    type PortfolioDeleteContext,
    type PortfolioDeleteActions,
} from './intent/portfolio/usePortfolioDelete';

// Planner Intent 훅
export {
    usePlannerFlow,
    type PlannerFlowContext,
    type PlannerFlowActions,
} from './intent/planner/usePlannerFlow';

export {
    usePlannerCompute,
    type PlannerComputeContext,
    type PlannerComputeActions,
} from './intent/planner/usePlannerCompute';

export {
    usePlannerStep,
    type PlannerStepKey,
    type PlannerStepViewParams,
    type PlannerStepSubmitParams,
    type PlannerStepActions,
} from './intent/planner/usePlannerStep';

// Auth Tracker 헬퍼
export {
    trackAuthCompletion,
    appendAuthIntentToUrl,
    removeAuthParamsFromQuery,
    type TrackAuthCompletionParams,
    type TrackAuthCompletionResult,
} from '../auth-tracker';

// Tracked Navigation 헬퍼
export {
    useTrackedNavigation,
    type NavigateWithTrackingParams,
} from './useTrackedNavigation';

